"use strict";

import * as constants from "./constants";

// スクロールバー管理
const { OverlayScrollbars } = typeof OverlayScrollbarsGlobal !== "undefined" ? OverlayScrollbarsGlobal : { OverlayScrollbars: undefined };

// ローディング管理
const loader = document.getElementById("loader");
let loadingCounter = 0;

/**
 * アクセスデバイスの閲覧状態が横長であるかをブラウザ表示サイズで判定します
 * @returns {boolean}
 */
export function isLandscape() {
  // 横が縦より長ければ横長と判定（正方形は横長と扱う）
  return window.innerWidth >= window.innerHeight;
}

/**
 * カテゴリ要素か判定します
 * @param {HTMLElement} targetCategoryElement
 * @returns {boolean}
 */
export function isCategory(targetCategoryElement) {
  return targetCategoryElement && targetCategoryElement.classList.contains("category");
}

/**
 * カテゴリ要素が表示されているか判定します
 * @param {HTMLElement} targetCategoryElement
 * @returns {boolean}
 */
export function isViewCategory(targetCategoryElement) {
  if (!isCategory(targetCategoryElement)) return false;
  const computedStyle = window.getComputedStyle(targetCategoryElement);
  return computedStyle.display === "flex";
}

/**
 * カテゴリ要素が開いているか判定します
 * @param {HTMLElement} targetCategoryElement
 * @returns {boolean}
 */
export function isOpenCategory(targetCategoryElement) {
  if (!isViewCategory(targetCategoryElement)) return false;
  const booksElement = [...targetCategoryElement.children].find((el) => el.matches(".books"));
  return booksElement && booksElement.style.display === "flex";
}

/**
 * フロアマップが開いているか判定します
 */
export function isOpenFloorMap() {
  return document.getElementById("sidebar")?.classList.contains("active") || false;
}

/**
 * タテオキから開かれた新しいタブか判定します
 */
export function isNewTab() {
  return window.name === constants.TATEOKI_TAB_NAME;
}

/**
 * Apple製デバイスかどうかを判定します
 * @returns {boolean} Apple製デバイスかどうか
 */
export function isAppleDevice() {
  const ua = navigator.userAgent;

  // iPhone, iPod, iPad (iPadOS 13 未満)
  if (isiPhone()) return true;

  // iPadOS 13 以降
  if (isiPad()) return true;

  // Mac (デスクトップ)
  if (isMac()) return true;

  return false;
}

/**
 * iPhone, iPod, iPad (iPadOS 13 未満) かどうかを判定します
 * @returns {boolean} iPhone, iPod, iPad (iPadOS 13 未満) かどうか
 */
export function isiPhone() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return true;
  }
  return false;
}

/**
 * iPadOS 13 以降かどうかを判定します
 * @returns {boolean} iPadOS 13 以降かどうか
 */
export function isiPad() {
  const ua = navigator.userAgent;
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return true;
  }
  return false;
}

/**
 * Mac (デスクトップ) かどうかを判定します
 * @returns {boolean} Mac (デスクトップ) かどうか
 */
export function isMac() {
  const ua = navigator.userAgent;
  if (/Macintosh/i.test(ua)) {
    return true;
  }
  return false;
}

/**
 * Safari ブラウザかどうかを判定します
 * @returns {boolean} Safari ブラウザかどうか
 */
export function isSafari() {
  const ua = navigator.userAgent;
  if (ua.indexOf("Safari") !== -1 && ua.indexOf("Chrome") === -1) {
    return true;
  }
  return false;
}

/**
 * iframe 内で表示されているか判定します
 * @param {*} gon
 * @returns
 */
export function isIframe(gon) {
  if (gon.iframe && JSON.parse(gon.iframe)) return true;
  return false;
}

/**
 * カテゴリ要素の高さに合わせた調整済みの高さを取得します
 * @param {*} categoriesElement
 * @returns
 */
export function getAdjustedHeight(categoriesElement) {
  const HEIGHT_SCALE = 0.98;
  const HEIGHT_MARGIN = 8;
  const clientHeight = categoriesElement.clientHeight;
  return clientHeight * HEIGHT_SCALE - HEIGHT_MARGIN;
}

/**
 * ローディングアニメーションを開始します
 */
export function showLoader() {
  if (!loader) return;
  loadingCounter++;

  // 表示済みの場合は終了
  if (1 < loadingCounter) return;

  loader.classList.add("loading");
}

/**
 * ローディングアニメーションを終了します
 */
export function hideLoader() {
  if (!loader) return;
  loadingCounter--;

  if (loadingCounter <= 0) {
    loadingCounter = 0;

    // 全ての処理が完了したらローディングを終了
    loader.classList.remove("loading");
  }
}

/**
 * モニタ表示エリア内の指定された位置に位置するエレメントを取得します
 * @param {string} position - ELEMENT_POSITION
 * @param {number} [customPosition] - "custom" 位置を使用する場合に指定するピクセル位置
 * @returns {Element|null}
 */
export function getElementAtPosition(position, customPosition = 0) {
  const windowWidth = window.innerWidth;
  let targetPosition;

  switch (position) {
    case constants.ELEMENT_POSITION.LEFT:
      targetPosition = window.scrollX + constants.CATEGORY_MARGIN_WIDTH;
      break;
    case constants.ELEMENT_POSITION.CENTER:
      targetPosition = window.scrollX + windowWidth / 2;
      break;
    case constants.ELEMENT_POSITION.RIGHT:
      targetPosition = window.scrollX + windowWidth;
      break;
    case constants.ELEMENT_POSITION.CUSTOM:
      targetPosition = window.scrollX + customPosition;
      break;
    default:
      throw new Error("無効な position 引数です。ELEMENT_POSITION を指定してください。");
  }

  // 範囲内にあるかどうかをチェック
  function isInRange(elementStart, elementEnd) {
    return elementStart <= targetPosition && targetPosition <= elementEnd;
  }

  // reverse することで最も深い要素からマッチングをおこなう
  const elements = [...document.querySelectorAll(".category")].reverse();

  for (let element of elements) {
    const spine = [...element.children].find((child) => child.classList.contains("category_spine_link"));
    const spineRect = spine.getBoundingClientRect();
    let elementStart = window.scrollX + spineRect.left;
    let elementEnd = window.scrollX + spineRect.right;

    // カテゴリ背表紙部の領域判定
    if (isInRange(elementStart, elementEnd)) {
      return element;
    }

    const info = [...element.children].find((child) => child.classList.contains("category-info"));
    const infoRect = info.getBoundingClientRect();
    elementStart = window.scrollX + infoRect.left;
    elementEnd = window.scrollX + infoRect.right;

    // カテゴリ詳細情報の領域判定
    if (isInRange(elementStart, elementEnd)) {
      return info;
    }

    const books = [...element.children].find((child) => child.classList.contains("books"));
    const booksRect = books.getBoundingClientRect();
    elementStart = window.scrollX + booksRect.left;
    elementEnd = window.scrollX + booksRect.right;

    // カテゴリに所属する背表紙部の領域判定
    if (isInRange(elementStart, elementEnd)) {
      return books;
    }
  }

  return null;
}

/**
 * 要素がターゲット位置の範囲内にあるかを判定します
 * @param {Element} positionElement - 位置の基準となる要素
 * @param {string} position - 位置の指定 ('CENTER' or 'LEFT')
 * @returns {boolean} - 要素が指定位置の範囲内にあるかどうか
 */
export function isElementWithinBounds(positionElement, position) {
  // 対象要素の背表紙リンクを取得
  const categorySpine = positionElement.querySelector(".category_spine_link");
  if (!categorySpine) return false;

  // 要素の境界を取得
  const rect = categorySpine.getBoundingClientRect();

  // 要素の左端と右端の位置を取得（スクロール位置も考慮）
  const elementStart = rect.left + window.scrollX;
  const elementEnd = rect.right + window.scrollX;

  // 要素が中央に配置されているかの判定
  const isCentered = position === constants.ELEMENT_POSITION.CENTER;

  // 中央に配置されている場合はスクロール位置にウィンドウ幅の半分を加えた位置をターゲットとする
  // 左揃えの場合はスクロール位置そのものをターゲット位置とする
  const targetPosition = isCentered ? window.scrollX + window.innerWidth / 2 : window.scrollX;

  // 要素がターゲット位置の範囲内にあるかどうかを判定
  const isWithinBounds = elementStart + constants.CATEGORY_MARGIN_WIDTH <= targetPosition && targetPosition <= elementEnd;

  // 要素が範囲内にあるかどうかを返す
  return isWithinBounds;
}

/**
 * OverlayScrollbars を初期化します
 * OverlayScrollbars が読み込まれていない場合は null を返します
 * @param {Element} container
 * @return {OverlayScrollbars|null} - 初期化されたインスタンスまたは null
 */
export function initScrollbar(container) {
  if (!OverlayScrollbars) return null;

  if (container) {
    // OverlayScrollbarsを初期化
    const osInstance = OverlayScrollbars(container, {
      // オプション設定 (macOS のような表示にするための設定)
      overflow: {
        x: "scroll",
        y: "scroll",
      },
      scrollbars: {
        visibility: "auto", // スクロール時のみ表示
        autoHide: "scroll", // マウスが領域から離れると非表示
        autoHideDelay: 500, // 非表示になるまでの遅延
      },
    });
    return osInstance;
  }
  return null;
}

/**
 * トグルボタンを管理するモジュール
 * - イベント登録
 * - SPA遷移後の状態更新
 * - サイドバー閉じ処理
 */
export function createToggleButtonManager(spa, setInternalDispatchEvent) {
  /** 登録されたボタン情報一覧 */
  const buttons = [];

  /**
   * トグルボタンを登録
   * @param {Object} options
   * @param {string} options.btnId ボタンのID
   * @param {string} options.labelId ラベル要素のID
   * @param {string} options.defaultLabel 非選択時のテキスト
   * @param {string} options.activeLabel 選択時のテキスト
   * @param {string} [options.requiredSelector] ボタン表示のために必要な要素セレクタ（存在しない場合は非表示）
   */
  function register({ btnId, labelId, defaultLabel, activeLabel, requiredSelector }) {
    const btn = document.getElementById(btnId);
    const label = document.getElementById(labelId);

    if (!btn || !label) return;

    if (requiredSelector && !document.querySelector(requiredSelector)) {
      btn.style.display = "none";
      return;
    }

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const isSelected = btn.classList.toggle("selected-btn");

      if (isSelected) {
        label.textContent = activeLabel;

        closeSidebarIfOpen();
        history.replaceState(null, "", btn.href);
        setInternalDispatchEvent(btn, false)(new Event("click"));
      } else {
        spa.changeView(constants.DEFAULT_VIEW_SELECTOR);
        label.textContent = defaultLabel;
      }
    });

    buttons.push({ btn, label, defaultLabel, activeLabel });
  }

  /**
   * SPA遷移後にボタン状態を更新
   * @param {string} currentViewSelector 遷移後のビューセレクタ
   */
  function updateStates(currentViewSelector) {
    buttons.forEach(({ btn, label, defaultLabel, activeLabel }) => {
      if (btn.dataset.viewSelector === currentViewSelector) {
        btn.classList.add("selected-btn");
        label.textContent = activeLabel;
      } else {
        btn.classList.remove("selected-btn");
        label.textContent = defaultLabel;
      }
    });
  }

  /** サイドバーを閉じる */
  function closeSidebarIfOpen() {
    const sidebar = document.getElementById("sidebar");
    if (sidebar?.classList.contains("active")) {
      document.getElementById("close-sidebar")?.click();
    }
  }

  return { register, updateStates };
}
